import { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  Users,
  Target,
  Package,
  Boxes,
  Star,
  GitBranch,
    Briefcase,
    Clock,
    ShieldCheck,
    Truck,
    Settings,
    type LucideIcon,
  Factory,
} from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import packageJson from "../../../package.json";

interface BaseRoute {
  name: string;
  href: string;
  icon: LucideIcon;
  requiredPermission?: string;
  badge?: string | number;
  isPremium?: boolean;
  requiredPlan?: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, getUserPlan, can } = useAuth();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const plan = getUserPlan();

  const isActiveRoute = useCallback(
    (href: string) => {
      if (href === "/dashboard") {
        return location.pathname === "/dashboard" || location.pathname === "/";
      }
      return (
        location.pathname === href || location.pathname.startsWith(`${href}/`)
      );
    },
    [location.pathname],
  );

  const navigation = useMemo(() => {
    const mainRoutes: BaseRoute[] = [
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
        requiredPermission: "dashboard.view",
      },
      {
        name: "Clientes",
        href: "/clients",
        icon: Briefcase,
        requiredPermission: "client_companies.read",
      },
      /* {
        name: "Proveedores",
        href: "/suppliers",
        icon: Truck,
        requiredPermission: "contacts.read", // Using same permission for now
      }, */
      {
        name: "Cotizaciones",
        href: "/opportunities",
        icon: Target,
        requiredPermission: "opportunities.read",
      },
      {
        name: "Aprobaciones",
        href: "/approvals",
        icon: ShieldCheck,
        requiredPermission: "opportunities.approve",
      },
      {
        name: "Productos",
        href: "/products",
        icon: Package,
        requiredPermission: "products.read",
      },
      /* {
        name: "Inventario",
        href: "/inventory",
        icon: Boxes,
        requiredPermission: "inventory.read",
      },
      {
        name: "Maquinaria",
        href: "/machinery",
        icon: Factory,
        requiredPermission: "inventory.read",
      }, */
      {
        name: "Personal",
        href: "/employees",
        icon: Users,
        requiredPermission: "users.read",
      },
      {
        name: "Pipelines",
        href: "/pipelinesadmin",
        icon: GitBranch,
        requiredPermission: "pipelines.manage",
      },
      /* {
        name: "Asistencia",
        href: "/attendance",
        icon: Clock,
        requiredPermission: "attendance.read",
      }, */
    ];

    const routes: BaseRoute[] = mainRoutes.filter((route) => {
      return !route.requiredPermission || can(route.requiredPermission);
    });

    return routes;
  }, [can]);

  const getRoleDisplayName = (role: string | undefined) => {
    if (!role) return "Usuario";

    const roleMap: Record<string, string> = {
      "super admin": "Super Admin",
      admin: "Administrador",
      vendedor: "Vendedor",
      lector: "Lector",
      manager: "Gerente",
    };
    return roleMap[role.toLowerCase()] || role;
  };

  const handleUpgradeClick = useCallback(() => {
    navigate("/planes");
  }, [navigate]);

  const handleUserClick = useCallback(() => {
    navigate("/settings");
  }, [navigate]);

  const handleOrgSettingsClick = useCallback(() => {
    navigate("/organization/settings");
  }, [navigate]);

  const primaryColor = company?.primary_color || "#3b82f6";
  const secondaryColor = company?.secondary_color || "#8b5cf6";

  return (
    <TooltipProvider delayDuration={150}>
      <style>{`
        :root {
          --sidebar-primary: ${primaryColor};
          --sidebar-secondary: ${secondaryColor};
        }
        .sidebar-active-gradient {
          background: linear-gradient(135deg, var(--sidebar-primary) 0%, var(--sidebar-secondary) 100%);
          box-shadow: 0 10px 15px -3px rgba(var(--sidebar-primary-rgb, 59, 130, 246), 0.3);
        }
        .sidebar-avatar-gradient {
          background: linear-gradient(135deg, var(--sidebar-primary) 0%, var(--sidebar-secondary) 100%);
          box-shadow: 0 4px 10px -2px rgba(var(--sidebar-primary-rgb, 59, 130, 246), 0.3);
        }
        .sidebar-badge-primary {
          background-color: var(--sidebar-primary);
        }
        .sidebar-text-primary {
          color: var(--sidebar-primary);
        }
      `}</style>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-auto max-w-[90%]">
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl shadow-black/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center px-2">
              <div
                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => navigate("/dashboard")}
                title="Ir al Dashboard"
              >
                {company?.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company?.name || "Logo"}
                    className="h-12 w-12 object-contain drop-shadow-md"
                  />
                ) : (
                  <div className="h-12 w-12 flex items-center justify-center bg-slate-800 rounded-lg shadow-inner">
                    <Building2 className="h-8 w-8 text-slate-500" />
                  </div>
                )}
                <span className="text-[9px] text-slate-500 font-medium -mt-1">
                  v{packageJson.version}
                </span>
              </div>
            </div>

            <div className="w-px h-10 bg-slate-700"></div>

            {navigation.map((item, index) => {
              const isActive = isActiveRoute(item.href);
              const isHovered = hoveredIndex === index;
              const Icon = item.icon;

              const scale = isHovered ? 1.3 : 1;
              const translateY = isActive ? -6 : 0;

              return (
                // eslint-disable-next-line
                <div
                  key={item.href}
                  className={`relative group transition-all duration-300 ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{
                    transitionDelay: `${index * 30}ms`,
                  }}
                >
                  <Link
                    to={item.href}
                    className="flex flex-col items-center justify-center"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div
                      className={cn(
                        "relative p-3 rounded-xl transition-all duration-300",
                        isActive
                          ? "sidebar-active-gradient shadow-lg -translate-y-1.5"
                          : "bg-slate-800/50 hover:bg-slate-800",
                        isHovered ? "scale-[1.3]" : "scale-100"
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 transition-colors",
                          isActive
                            ? "text-white"
                            : "text-slate-400 group-hover:text-white",
                        )}
                      />

                      {item.badge && (
                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-white text-xs border-2 border-slate-900 sidebar-badge-primary">
                          {typeof item.badge === "number" && item.badge > 9
                            ? "9+"
                            : item.badge}
                        </Badge>
                      )}

                      {isActive && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-lg shadow-white/50"></div>
                      )}
                    </div>
                  </Link>

                  <div
                    className={cn(
                      "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-lg shadow-xl whitespace-nowrap transition-all duration-200",
                      isHovered
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2 pointer-events-none",
                    )}
                  >
                    <p className="text-xs font-medium text-white">
                      {item.name}
                    </p>
                    {item.isDisabled && (
                      <p className="text-xs text-orange-600 font-medium mt-1">
                        Requiere upgrade
                      </p>
                    )}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-slate-900/95 border-r border-b border-slate-800 rotate-45"></div>
                  </div>
                </div>
              );
            })}

            <div className="w-px h-10 bg-slate-700"></div>

            {/* eslint-disable-next-line */}
            <div
              className={`relative group transition-all duration-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: `${navigation.length * 30}ms`,
              }}
              onMouseEnter={() => setHoveredIndex(navigation.length)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div
                className="flex items-center gap-2 p-2 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-all duration-300 cursor-pointer"
                onClick={handleUserClick}
              >
                <div 
                  className="w-10 h-10 shrink-0 aspect-square rounded-full flex items-center justify-center text-white font-semibold text-sm ring-1 ring-white/10 sidebar-avatar-gradient"
                >
                  {user?.first_name?.charAt(0) ||
                    user?.email?.charAt(0)?.toUpperCase() ||
                    "U"}
                </div>
              </div>

              <div
                className={cn(
                  "absolute bottom-full left-1/2 -translate-x-1/2 mb-0 pb-3 px-3 py-2 bg-transparent whitespace-nowrap transition-all duration-200 min-w-[180px] z-50",
                  hoveredIndex === navigation.length
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none",
                )}
              >
                <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-800 rounded-lg shadow-xl p-3 w-56">
                <p className="text-sm font-medium text-white mb-1 break-words whitespace-normal">
                  {user?.first_name
                    ? `${user.first_name} ${user.last_name || ""}`
                    : user?.name || "Usuario"}
                </p>
                <p className="text-xs text-slate-400 mb-1 break-all whitespace-normal">{user?.email}</p>
                <p className="text-xs font-medium mb-2 sidebar-text-primary truncate">
                  {getRoleDisplayName(user?.role?.name)}
                </p>

                <div className="flex flex-col gap-1 mb-2">
                    <Button
                        className="w-full bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium py-1 h-7 justify-start"
                        size="sm"
                        onClick={handleUserClick}
                    >
                        <Users className="h-3 w-3 mr-2" />
                        Mi Perfil
                    </Button>
                    
                    {can('settings.manage') && (
                        <Button
                            className="w-full bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium py-1 h-7 justify-start"
                            size="sm"
                            onClick={handleOrgSettingsClick}
                        >
                            <Building2 className="h-3 w-3 mr-2" />
                            Configurar Empresa
                        </Button>
                    )}
                </div>

                {plan.type !== "enterprise" && (
                  <Button
                    className="w-full text-white text-xs font-medium py-1 h-8 sidebar-active-gradient border-none hover:opacity-90"
                    size="sm"
                    onClick={handleUpgradeClick}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Actualizar Plan
                  </Button>
                )}

                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-4 w-2 h-2 bg-slate-900/95 border-r border-b border-slate-800 rotate-45"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
